import styled, { css, keyframes } from 'styled-components';
import { DX } from '../../../../theme/v2';
import Button from '../../../../components/Button';

/* ── The sheet arriving ──────────────────────────────────────────────────────
   It comes up from the edge it is attached to. A panel that simply exists, full of grey skeleton
   rows, gives the eye no idea where it came from or how to dismiss it; one that rises from the
   bottom has said both before it has finished moving.

   `Sheet` and `Backdrop` are passed to react-modal as its content and overlay elements, so its
   classes land on components of ours rather than on the global `ReactModal__Content`, which every
   modal in the app wears — including the seventeen legacy ones. */
const rise = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: none;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const Sheet = styled.div`
  /* The curve of something heavy that was pushed: quick to leave, slow to settle. A symmetrical
     ease reads like a slide show. */
  animation: ${rise} 320ms cubic-bezier(0.32, 0.72, 0, 1);
`;

export const Backdrop = styled.div`
  animation: ${fadeIn} 220ms ${DX.ease};
`;

/* After the sheet has arrived, not during. Eight skeleton rows travelling up the screen are motion
   about nothing; the sheet moves, then it fills. */
export const sheetFill = css`
  animation: ${fadeIn} 200ms ${DX.ease} 200ms backwards;
`;

/* A sheet across the foot of the window rather than a box in the middle of it: the table it carries
   is wide, and a centred modal spent half the screen on margins. Its height follows its contents
   between two fifths and nine tenths of the window — enough that three rows do not rattle around and
   forty do not run off the bottom. */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 40vh;
  max-height: 90vh;
  padding: 0;
  background: ${DX.surface};
  color: ${DX.text};
`;

export const LegacyContainer = styled.div`
  width: 80vw;
  position: relative;
`;

export const TableContainerStyle = styled.div`
  height: 70vh;
  overflow: auto;
  table {
    thead {
      position: sticky;
      top: 0px;
      z-index: 100;
      background: light-dark(#f8f9fa, var(--mantine-color-dark-6));
      box-shadow:
        0 -1px 0 light-dark(#f1f3f5, var(--mantine-color-dark-6)),
        0 1px 0 light-dark(#f1f3f5, var(--mantine-color-dark-6));
    }
  }
`;

export const CloseStyle = styled.div`
  position: absolute;
  display: flex;
  right: 0;
  top: 0;
  cursor: pointer;
`;

export const Title = styled.p`
  color: light-dark(#25262b, var(--mantine-color-dark-0));
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  margin-bottom: 30px;
`;

export const ErrorMessageStyle = styled.p`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  /* A flex box clips its children, it does not ellipsise them. A whole error message in a column
     two hundred pixels wide was cut mid-word with no sign that anything had been cut — so the
     sentence is what gives way, it says that it did, and the glyph after it stays whole. */
  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Something to fix, in the colour the rest of the app uses for it. */
  &[data-tone='warning'] {
    color: ${DX.tone.warning};
  }

  /* Several reasons are a list, not a paragraph: one per line, so the numbering means something. */
  .line {
    display: block;
  }

  .line + .line {
    margin-top: 2px;
  }
`;

export const DescriptionStyle = styled.div`
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  min-width: 0;

  /* Whatever the row has to say about itself takes the space; the way into the sheet keeps its
     place at the end of the row. */
  > *:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The way in keeps the right edge of its column, whatever the sentence before it did. Following
     the last word puts it somewhere different on every row, and forty links at forty different
     offsets are not a column — which is why the legacy table gave the arrow one of its own. */
  > button:last-child {
    margin-left: auto;
  }
`;

/* The chip is the progress bar.
   A separate track beside it needed a column half again as wide for a state that is true of one row
   in twenty; filling the chip itself says the same thing in the space the chip already takes. */
export const ProgressCellStyle = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

/* Black at or above 85: a score that is fine is not news, and a column of green said otherwise
   twenty times. Colour is left for the ones worth stopping at. */
export const ConfidenceButtonStyle = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${({ $score }) =>
    $score == null
      ? DX.textMuted
      : $score >= 85
        ? DX.text
        : $score >= 70
          ? DX.tone.warning
          : DX.tone.attention};
  cursor: pointer;
  font-size: ${DX.fz.sm};
  line-height: ${DX.lh.sm};
  font-variant-numeric: tabular-nums;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
  }
`;

export const DetailsModalBodyStyle = styled.div`
  position: relative;
  width: min(960px, 86vw);
  max-height: 78vh;
  overflow: auto;
  padding: 4px 4px 0;
`;

export const DetailsHeaderStyle = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 20px;
  align-items: center;
  padding: 20px;
  margin-top: 12px;
  border: 1px solid light-dark(#e7f5ff, var(--mantine-color-blue-light));
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    light-dark(#f8fbff, var(--mantine-color-blue-light)) 0%,
    light-dark(#eef8ff, var(--mantine-color-blue-light)) 100%
  );
`;

export const ScoreRingStyle = styled.div`
  width: 116px;
  height: 116px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(
      circle at center,
      light-dark(#fff, var(--mantine-color-dark-6)) 58%,
      transparent 59%
    ),
    conic-gradient(
      ${({ $color, $score }) => $color} ${({ $score }) => $score}%,
      light-dark(#dee2e6, var(--mantine-color-dark-4)) 0
    );
  box-shadow: 0 8px 24px rgba(34, 139, 230, 0.12);
`;

export const StatusPillStyle = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $tone }) => $tone?.background || 'light-dark(#f1f3f5, var(--mantine-color-dark-6))'};
  color: ${({ $tone }) => $tone?.color || 'light-dark(#495057, var(--mantine-color-dark-1))'};
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
`;

export const DetailGridStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
`;

export const DetailCardStyle = styled.div`
  padding: 14px;
  border: 1px solid light-dark(#e9ecef, var(--mantine-color-dark-5));
  border-radius: 12px;
  background: var(--mantine-color-body);
  box-shadow: 0 6px 18px rgba(33, 37, 41, 0.04);
`;

export const SectionHeaderStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 22px 0 10px;
`;

export const VerdictSummaryStyle = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const VerdictPillStyle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid
    ${({ $tone, $active }) => ($active ? $tone?.color : $tone?.border) || 'light-dark(#dee2e6, var(--mantine-color-dark-4))'};
  border-radius: 999px;
  background: ${({ $tone }) => $tone?.background || 'light-dark(#f1f3f5, var(--mantine-color-dark-6))'};
  color: ${({ $tone }) => $tone?.color || 'light-dark(#495057, var(--mantine-color-dark-1))'};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  box-shadow: ${({ $active, $tone }) =>
    $active
      ? `0 0 0 2px ${$tone?.border || 'light-dark(#dee2e6, var(--mantine-color-dark-4))'}`
      : 'none'};

  &:hover:not(:disabled) {
    filter: brightness(0.98);
  }
`;

export const EvidenceFilterMetaStyle = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  color: light-dark(#868e96, var(--mantine-color-dark-2));
  font-size: 11px;
  line-height: 16px;
`;

export const EvidenceListStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

export const EvidenceItemStyle = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 16px;
  border: 1px solid
    ${({ $tone }) => $tone?.border || 'light-dark(#e9ecef, var(--mantine-color-dark-5))'};
  border-radius: 12px;
  background: var(--mantine-color-body);
  color: light-dark(#25262b, var(--mantine-color-dark-0));
  font-size: 12px;
  line-height: 18px;
  box-shadow: 0 4px 14px rgba(33, 37, 41, 0.035);

  & > * {
    min-width: 0;
  }
`;

export const EvidenceHeaderStyle = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const EvidenceBadgeGroupStyle = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const EvidenceCategoryBadgeStyle = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: light-dark(#edf2ff, var(--mantine-color-indigo-light));
  color: light-dark(#364fc7, var(--mantine-color-indigo-light-color));
  font-size: 11px;
  font-weight: 700;
`;

export const FieldConfidenceBadgeStyle = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({ $score }) => ($score >= 85 ? 'light-dark(#EBFBEE, var(--mantine-color-green-light))' : $score >= 70 ? 'light-dark(#FFF4E6, var(--mantine-color-orange-light))' : 'light-dark(#FFF5F5, var(--mantine-color-red-light))')};
  color: ${({ $score }) => ($score >= 85 ? '#2F9E44' : $score >= 70 ? '#F08C00' : '#E03131')};
  font-size: 11px;
  font-weight: 800;
`;

export const EvidenceValueStyle = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid light-dark(#f1f3f5, var(--mantine-color-dark-6));
  border-radius: 10px;
  background: light-dark(#fbfcfd, var(--mantine-color-dark-6));
  margin-bottom: 8px;

  & > * {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const EvidenceReasonStyle = styled.div`
  padding-top: 10px;
  border-top: 1px solid light-dark(#f1f3f5, var(--mantine-color-dark-6));
  color: light-dark(#495057, var(--mantine-color-dark-1));
  font-size: 12px;
  line-height: 18px;
`;

export const SourceReferenceStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SourceMatchedValueStyle = styled.div`
  width: fit-content;
  padding: 6px 8px;
  border: 1px solid light-dark(#b2f2bb, var(--mantine-color-green-light-hover));
  border-radius: 8px;
  background: light-dark(#ebfbee, var(--mantine-color-green-light));
  color: light-dark(#2b8a3e, var(--mantine-color-green-light-color));
  font-size: 12px;
  font-weight: 700;
`;

export const SourceTableStyle = styled.div`
  display: grid;
  grid-template-columns: 70px 80px minmax(120px, 1fr);
  border: 1px solid light-dark(#e9ecef, var(--mantine-color-dark-5));
  border-radius: 8px;
  overflow: hidden;
  background: var(--mantine-color-body);
  max-width: 100%;
`;

export const SourceTableCellStyle = styled.div`
  padding: 6px 8px;
  border-bottom: 1px solid light-dark(#f1f3f5, var(--mantine-color-dark-6));
  border-right: 1px solid light-dark(#f1f3f5, var(--mantine-color-dark-6));
  color: ${({ $header }) => ($header ? 'light-dark(#868E96, var(--mantine-color-dark-2))' : 'light-dark(#25262B, var(--mantine-color-dark-0))')};
  font-size: 11px;
  font-weight: ${({ $header }) => ($header ? 700 : 400)};
  line-height: 16px;
  overflow-wrap: anywhere;
`;

export const NoteItemStyle = styled.div`
  padding: 10px 12px;
  border: 1px solid light-dark(#fff3bf, var(--mantine-color-yellow-light-hover));
  border-radius: 10px;
  background: light-dark(#fff9db, var(--mantine-color-yellow-light));
  color: light-dark(#5c3c00, var(--mantine-color-yellow-light-color));
  font-size: 12px;
  line-height: 18px;
`;

/* The file is what a row is called; it carries a step more type than the values beside it, the way
   the report's row labels stand out from the cells they run into. */
export const FileNameStyle = styled.div`
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  min-width: 0;

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${DX.fz.md};
  }
`;

export const FooterStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-top: 32px;

  button:disabled {
    svg {
      path {
        fill: transparent !important;
      }
    }
  }
`;

export const customStyles = {
  content: {
    top: 'auto',
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    maxWidth: '100%',
    marginRight: 0,
    // No `transform` here. It used to say `none`, to undo a centring the four edges above have
    // already undone — and inline it outranks the class that slides the sheet in, so the animation
    // ran against a property that could not move.
    // Height follows the contents between these two; the sections inside scroll rather than the
    // sheet growing past the window.
    height: 'auto',
    minHeight: '40vh',
    maxHeight: '90vh',
    overflow: 'hidden',
    // V2's own frame: one hairline, rounded where it meets the page, and no inner padding — the
    // sections inside bring their own.
    padding: 0,
    border: '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))',
    borderBottom: 'none',
    borderRadius: '12px 12px 0 0',
    boxShadow: '0 -8px 32px rgba(16, 24, 40, 0.12)',
    // Stay above the page table's sticky <thead> (z-index 9 in shared Table).
    zIndex: 1001
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.30)',
    zIndex: 1000
  }
};

export const detailsModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'hidden',
    border: '0',
    borderRadius: '18px',
    padding: '20px',
    zIndex: 1003
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.30)',
    zIndex: 1002
  }
};

/* The dot's colour, not the text's: the words stay the same black as the rest of the row and the
   state is the mark in front of them — the periods table's chip, and the reason a column of these
   reads as a column rather than as a column of warnings. */
export const colors = {
  Completed: DX.tone.ok,
  'Not started': DX.tone.idle,
  'In progress': DX.tone.next,
  Cancelled: DX.tone.idle,
  Failed: DX.tone.attention,
  'Not extracted': DX.tone.warning,
  /* Data is there, no run behind it — grey rather than green: nothing was measured, so there is no
     confidence score to stand next to it. */
  'Saved manually': DX.tone.idle,
  Manually: DX.tone.idle
};

/* The chip from the periods table, without its chevron: that one is a choice you can change, this
   one is a state you can only read. */
/* A run the backend reports no number for. The chip still has to look like it is moving — the row
   is alive — but it must not claim a position, so the fill sweeps instead of filling. */
const chipSweep = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -100% 0; }
`;

export const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${DX.space[2]};
  padding: 1px ${DX.space[3]};
  border: ${DX.hairline};
  border-radius: ${DX.radius.control};
  font-size: ${DX.fz.xs};
  line-height: ${DX.lh.sm};
  color: ${DX.text};
  white-space: nowrap;
  /* White under it, not the row: a chip that lets the hover through stops reading as a chip. What is
     running fills that white to the point it has got to. */
  background: ${({ $progress }) =>
    $progress == null
      ? DX.surface
      : `linear-gradient(to right, ${DX.accentSoft} 0 ${$progress}%, ${DX.surface} ${$progress}%)`};
  transition: background ${DX.duration} ${DX.ease};

  ${({ $indeterminate }) =>
    $indeterminate &&
    css`
      background: linear-gradient(
        90deg,
        ${DX.surface} 0%,
        ${DX.accentSoft} 40%,
        ${DX.accentSoft} 60%,
        ${DX.surface} 100%
      );
      background-size: 300% 100%;
      animation: ${chipSweep} 1.8s linear infinite;
      transition: none;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        background: ${DX.surface};
      }
    `}

  .mark {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $tone }) => $tone || DX.tone.idle};
  }

  .pct {
    color: ${DX.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

export const CancelButton = styled(Button)`
  width: 159px;
  background: var(--mantine-color-body);
  border: 1px solid #e03131;
  color: #e03131;
`;

/* ── V2 shell ────────────────────────────────────────────────────────────────
   The parts of this drawer the page can see: its head, its toolbar, the frame around the table and
   the row of actions at the foot. Everything below them — the columns, the progress, the confidence
   details — is the component this was copied from, unchanged. */

export const IssuesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${DX.space[4]};
  padding: ${DX.space[4]} ${DX.space[5]};
  border-bottom: ${DX.hairline};

  h3 {
    margin: 0;
    font-size: ${DX.fz.md};
    font-weight: ${DX.fw.strong};
    line-height: ${DX.lh.md};
    color: ${DX.text};
  }
`;

/* What this is, on the left; everything you can do to it, on the right. The heading had been running
   at the browser's own h3 — nearly 19px, twice the size of the captions under it — and the controls
   sat next to it in three different heights. */
export const IssuesToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  padding: ${DX.space[3]} ${DX.space[5]};

  h3 {
    margin: 0;
    font-size: ${DX.fz.md};
    font-weight: ${DX.fw.strong};
    line-height: ${DX.lh.md};
    color: ${DX.text};
    white-space: nowrap;
  }

  .spacer {
    flex: 1;
  }

  .count {
    font-size: ${DX.fz.xs};
    color: ${DX.textMuted};
    white-space: nowrap;
  }

  /* One height for the lot of them: the field, the menu, the buttons and the way out. */
  .mantine-Input-input,
  .mantine-Button-root,
  .mantine-ActionIcon-root {
    height: ${DX.control.base};
    min-height: ${DX.control.base};
    border-radius: ${DX.radius.control};
    font-size: ${DX.fz.sm};
  }

  .mantine-ActionIcon-root {
    width: ${DX.control.base};
  }

  .mantine-Input-input {
    border-color: ${DX.border};
    color: ${DX.text};
  }

  .mantine-Input-input::placeholder {
    color: ${DX.textFaint};
  }
`;

/* Columns of a stated width.
   They had been sized by whatever the browser made of their contents: the file name took a third of
   the window, the values after it drifted apart, and nothing lined up between one row and the next.
   Widths do that work now — the alignment is the structure, so no rules are needed between them. */
export const IssuesTable = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 ${DX.space[5]} ${DX.space[5]};

  ${sheetFill}

  table {
    table-layout: fixed;
    min-width: 1080px;
  }

  th,
  td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* A cell that carries something you have to read in full — a file name, a table name, the reason
     a sheet failed — says all of it and the row grows to hold it. An ellipsis in a table of nine
     columns saves a line and costs the sentence; the legacy table wrapped, and it was right to.

     It breaks inside a word when it has to. These names are one token with no spaces in it —
     ReviewedPack.pdubey1.SCHONFELDRIA-SSUKMAST_OM.20260201.20260228.Draft.xlsx — so "wrap" alone
     gives the line no place to break, and the cell had nothing to do but paint over the two columns
     to its right. Nothing overflows either: a cell that cannot fit its content hides the remainder
     rather than covering its neighbour. */
  td[data-wrap='true'] {
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
    padding-top: ${DX.space[2]};
    padding-bottom: ${DX.space[2]};
  }

  td[data-wrap='true'] p,
  td[data-wrap='true'] span {
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
  }

  /* The head keeps the grey strip the project list gives it. Both selectors carry the table so they
     outweigh DataTable's own row rules, which are written for a page rather than for nine columns
     in a sheet: 36px rows and 16px gutters put a third of this table into its margins. */
  table thead th {
    background: ${DX.surfaceSunken};
    padding: ${DX.space[2]} ${DX.space[5]};
  }

  /* The sort arrows arrived at 16px beside an 11px caption — half again the height of the word they
     belong to, and the loudest mark in the header. */
  table thead svg {
    width: 13px;
    height: 13px;
  }

  table td {
    height: 40px;
    padding: 0 ${DX.space[5]};
  }

  /* Most of these sheets are extracted by hand and can never be re-run: their boxes say so by
     standing empty, rather than filling the column with grey chips. */
  table .mantine-Checkbox-input:disabled {
    background: transparent;
    border-color: ${DX.border};
  }

  /* Nine columns is more than the eye tracks across on its own — the report lights the row under
     the cursor for the same reason. */
  table tbody tr:hover td {
    background: ${DX.hover};
  }
`;

/* One caption, one size. The head cells arrived from the legacy table each carrying its own <Text>,
   and nine columns in three sizes read as an unfinished screen. The th sets the type now; this only
   puts the sort arrows beside the word. */
export const HeadCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${DX.space[1]};
  min-width: 0;
`;

export const IssuesEmpty = styled.div`
  padding: ${DX.space[7]};
  text-align: center;
  font-size: ${DX.fz.sm};
  color: ${DX.textMuted};
`;

export const IssuesFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  padding: ${DX.space[4]} ${DX.space[5]};
  border-top: ${DX.hairline};

  .count {
    margin-right: auto;
    font-size: ${DX.fz.xs};
    color: ${DX.textMuted};
  }
`;

/* The way into the sheet used to be a 12px arrow with nothing beside it — a target you had to know
   was there. A word carries the click. Grey until the row is under the cursor: twenty blue links
   down the right edge were a column of their own, competing with the data they belong to. */
export const OpenSheetLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${DX.space[1]};
  flex-shrink: 0;
  padding: 2px ${DX.space[2]};
  border: none;
  border-radius: ${DX.radius.control};
  background: none;
  font-size: ${DX.fz.xs};
  line-height: ${DX.lh.xs};
  color: ${DX.textMuted};
  cursor: pointer;
  white-space: nowrap;
  transition: color ${DX.duration} ${DX.ease};

  /* Alone in the cell it stands where every other column's text stands; after a description it
     keeps the gap. The button's own padding is what would otherwise push it 6px out of line. */
  &:first-child {
    margin-left: calc(-1 * ${DX.space[2]});
  }

  tr:hover & {
    color: ${DX.accent};
  }

  &:hover {
    background: ${DX.hover};
    color: ${DX.accent};
  }
`;

/* ── The sheet arriving ──────────────────────────────────────────────────────
   It comes up from the edge it is attached to. A panel that simply exists, full
   of grey skeleton rows, gives the eye no idea where it came from or how to
   dismiss it; one that rises from the bottom has said both before it has
   finished moving.

   These are `contentElement` / `overlayElement` rather than a stylesheet on
   react-modal's own `ReactModal__Content` classes, which are global and worn by
   every modal in the app, including the seventeen legacy ones. */
